/**
 * Píxel de Meta — conjunto de datos «Gurumba Web» (1691445449224035), en el
 * portafolio gurumbacom y conectado a la cuenta publicitaria «Gurumba Ads».
 *
 * El código base vive en index.html (dispara el primer PageView). Aquí van
 * los eventos que Meta usa para optimizar la pauta:
 *   · PageView             cada cambio de ruta (la app es SPA)
 *   · CompleteRegistration cuenta creada (email o Google)
 *   · ViewContent          ficha de un curso en vivo
 *   · InitiateCheckout     abrió el pago de una entrada o de una membresía
 *   · Purchase             volvió de pagar una entrada
 *   · Subscribe            volvió de pagar En Vivo o Premium
 *
 * Sin datos personales: ni correo ni nombre. Si un bloqueador quita el
 * píxel, todo esto es no-op y la app sigue igual.
 */
import { LAUNCH_PROMO, isLaunchPromoActive } from '@/lib/promo';

type Fbq = (...args: unknown[]) => void;
export type MetaEvent =
  | 'PageView'
  | 'ViewContent'
  | 'CompleteRegistration'
  | 'InitiateCheckout'
  | 'Purchase'
  | 'Subscribe';

function fbq(): Fbq | null {
  try {
    const f = (window as unknown as { fbq?: Fbq }).fbq;
    return typeof f === 'function' ? f : null;
  } catch {
    return null;
  }
}

/** Manda un evento estándar. `eventId` sirve para no contarlo doble con la API de conversiones. */
export function metaTrack(event: MetaEvent, params?: Record<string, unknown>, eventId?: string): void {
  const f = fbq();
  if (!f) return;
  try {
    if (eventId) f('track', event, params ?? {}, { eventID: eventId });
    else f('track', event, params ?? {});
  } catch {
    // El píxel nunca puede romper la app.
  }
}

/** PageView en cada cambio de ruta. El primero ya lo manda index.html. */
let primeraVista = true;
export function metaPageView(): void {
  if (primeraVista) { primeraVista = false; return; }
  metaTrack('PageView');
}

type Familia = 'pro' | 'premium';
const CLAVE_CHECKOUT = 'gurumba.meta.checkout';

/** Lo que paga hoy por la membresía (con el cupón de lanzamiento si sigue vigente). */
export function precioMembresia(family: Familia): number {
  const p = LAUNCH_PROMO[family];
  return (isLaunchPromoActive() ? p.priceCents : p.regularCents) / 100;
}

function nombreMembresia(family: Familia): string {
  return family === 'premium' ? 'Premium' : 'En Vivo';
}

function esFamilia(v: unknown): v is Familia {
  return v === 'pro' || v === 'premium';
}

/** Abrió el pago de una membresía: InitiateCheckout y se recuerda cuál era para el Subscribe. */
export function metaCheckoutMembresia(family: Familia): void {
  try { sessionStorage.setItem(CLAVE_CHECKOUT, family); } catch { /* sin storage */ }
  metaTrack('InitiateCheckout', {
    content_category: 'membresia',
    content_name: nombreMembresia(family),
    value: precioMembresia(family),
    currency: 'USD',
  });
}

/**
 * Volvió de pagar la membresía. Sin familia conocida, usa la del checkout.
 * `eventId` = el id de la sesión de Stripe (?sid=), el mismo que manda el
 * webhook por la API de conversiones: Meta cuenta una sola.
 */
export function metaSuscripcion(family?: Familia | null, eventId?: string | null): void {
  let f: Familia | null = family ?? null;
  if (!f) {
    try {
      const guardada = sessionStorage.getItem(CLAVE_CHECKOUT);
      f = esFamilia(guardada) ? guardada : null;
    } catch { /* sin storage */ }
  }
  try { sessionStorage.removeItem(CLAVE_CHECKOUT); } catch { /* sin storage */ }
  const id = eventId || undefined;
  if (!f) { metaTrack('Subscribe', { currency: 'USD' }, id); return; }
  metaTrack('Subscribe', {
    content_name: nombreMembresia(f),
    content_category: 'membresia',
    value: precioMembresia(f),
    currency: 'USD',
    predicted_ltv: precioMembresia(f) * 12,
  }, id);
}

/** Cookies del píxel (_fbp y _fbc) para mandarlas al abrir el pago (API de conversiones). */
export function metaCookies(): { fbp: string | null; fbc: string | null } {
  try {
    const leer = (n: string) => document.cookie.split('; ').find((c) => c.startsWith(`${n}=`))?.slice(n.length + 1) ?? null;
    return { fbp: leer('_fbp'), fbc: leer('_fbc') };
  } catch {
    return { fbp: null, fbc: null };
  }
}

/** Agrega ?sid={CHECKOUT_SESSION_ID} al success_url: Stripe lo cambia por el id de la sesión. */
export function conSid(url: string): string {
  if (url.includes('{CHECKOUT_SESSION_ID}')) return url;
  return `${url}${url.includes('?') ? '&' : '?'}sid={CHECKOUT_SESSION_ID}`;
}

/** Puente desde los eventos de producto (analytics.track) a los de Meta. */
export function metaDesdeProducto(event: string, props?: Record<string, unknown>): void {
  if (event === 'signup_completed') {
    const id = typeof props?.user_id === 'string' ? `reg-${props.user_id}` : undefined;
    metaTrack('CompleteRegistration', { status: true }, id);
  } else if (event === 'subscription_checkout_started') {
    if (esFamilia(props?.family)) metaCheckoutMembresia(props.family);
  } else if (event === 'subscription_activated') {
    metaSuscripcion(null, typeof props?.sid === 'string' ? props.sid : null);
  }
}
